import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { AddIcon } from 'icons/materialIcons';
import TeamLinksToggle from './TeamLinksToggle';
import TeamRecommendedLinks from './TeamRecommendedLinks';
import TeamContactLinksList from './TeamContactLinksList';
import TeamContactLinkDetailModal from './TeamContactLinkDetailModal';
import TeamAddContactLinkModal from './TeamAddContactLinkModal';
import TeamUpdateContactLink from './TeamUpdateContactLink';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { createCard, updateCard, updateCardContactLinks, getCardById } from 'apis/cardApi';
import { validateUUID } from 'utils/validator';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { updateCardProfilePictureUrl, updateCardCoverPhotoUrl, updateCardCompanyLogoUrl } from 'apis/cardApi';
import { API_BASE_URL } from 'utils/apiUtils';
import { useUserPreferences } from 'contexts/UserPreferencesContext';
import { useTeam } from 'team/contexts/teamContext';
import { getUserTeams } from 'team/apis/teamApi';
import { getSubteam } from 'team/apis/teamSubteamApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { isPhoneContactType } from 'utils/phoneUtils';

const TeamContactLinksContainer = ({ user, setSelectedItem }) => {
    const {
        contactLinks,
        setCurrentCard,
        fetchContactLinksForCard,
        updateContactLink: contextUpdateContactLink,
        deleteContactLink: contextDeleteContactLink,
        addContactLink: contextAddContactLink,
        transferTemporaryLinks,
        saveAllContactLinksToDatabase, // ✅ ADD: Import the UUID transfer function
        clearFetchedCards,
        isInSignupFlow,
        contactLinksByCard,
        currentCardId,
        debugCurrentState
    } = useContactLinkContext();

    const [contactLinksDetailModalOpen, setContactLinksDetailModalOpen] = useState(false);
    const [selectedItem, setSelectedLinkItem] = useState('');
    const [contactLinkType, setContactLinkType] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { cardId } = useParams();
    const { authState } = useAuth();
    const { getDefaultFollowUpEmail } = useUserPreferences();
    const { currentTeamId } = useTeam();
    // Use the member's userId if available (from props), otherwise fallback to logged-in user
    const userId = user?.userId || authState?.user?.userId;
    const [shouldRefresh, setShouldRefresh] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Content restrictions state
    const [contentRestrictions, setContentRestrictions] = useState({
        restrictAll: false,
        restrictedLinks: {}
    });
    const [loadingRestrictions, setLoadingRestrictions] = useState(false);
    const [isTeamAdmin, setIsTeamAdmin] = useState(false);
    const [isSubteamAdmin, setIsSubteamAdmin] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'warning'
    });

    // PRODUCTION-READY: Enhanced card creation detection
    const isCreatingCard = () => {
        if (!cardId) return false;

        // Check if it's a UUID (contains hyphens and is 32-36 chars)
        const isUUID = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

        // Check if it's a MongoDB ObjectId (24 chars, hex, no hyphens)
        const isMongoId = cardId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cardId) && !cardId.includes('-');

        // UUIDs indicate card creation, MongoDB IDs are existing cards
        return isUUID;
    };

    const isTemporaryCard = isCreatingCard();

    // 🔍 DEBUG: Log userId determination
    console.log('🔍 TeamContactLinksContainer - userId determination:', {
        userProp: user,
        userUserId: user?.userId,
        authUserId: authState?.user?.userId,
        finalUserId: userId,
        isCreatingCard: isTemporaryCard,
        cardId: cardId?.substring(0, 12) + '...'
    });

    const {
        cardState,
        updateCardId,
        setCardProfilePictureUrl,
        setCardCoverPhotoUrl,
        setCardCompanyLogoUrl,
        updateCardData,
        loadCardFromServer,
        isTemporaryCard: contextIsTemporaryCard
    } = useCardContext();

    useEffect(() => {
        if (authState.user && authState.user.userId) {
            // setUserId(authState.user.userId); // This line is removed as per the edit hint
        }
    }, [authState.user]);

    // Load content restrictions for the current user
    const loadContentRestrictions = useCallback(async () => {
        if (!userId || isTemporaryCard) return; // Skip for new cards or if no user

        setLoadingRestrictions(true);
        try {
            const teamsResponse = await getUserTeams();
            const teams = teamsResponse?.teams || teamsResponse || [];

            const subteamIds = [];
            let userIsTeamAdmin = false;
            let userIsSubteamAdmin = false;

            teams.forEach(team => {
                const membership = team.membershipInfo || team;
                if (membership.role === 'admin') {
                    userIsTeamAdmin = true;
                }
                if (membership.isSubteamAdmin) {
                    userIsSubteamAdmin = true;
                }
                if (membership.subteam?._id) {
                    subteamIds.push(membership.subteam._id);
                }
            });

            setIsTeamAdmin(userIsTeamAdmin);
            setIsSubteamAdmin(userIsSubteamAdmin);

            // If user is admin, no restrictions apply
            if (userIsTeamAdmin || userIsSubteamAdmin) {
                setLoadingRestrictions(false);
                return;
            }

            const mergedRestrictions = {
                restrictAll: false,
                restrictedLinks: {}
            };

            // Load restrictions from all subteams (if any subteam restricts, it's restricted)
            for (const subteamId of subteamIds) {
                try {
                    const subteamResponse = await getSubteam(subteamId);
                    const subteam = subteamResponse?.subteam || subteamResponse;
                    const subteamRestrictions = subteam?.settings?.restrictions || {};

                    if (subteamRestrictions.content) {
                        const contentRestrictions = subteamRestrictions.content;
                        // If any subteam has restrictAll, it's restricted
                        if (contentRestrictions.restrictAll) {
                            mergedRestrictions.restrictAll = true;
                        }
                        // Merge link restrictions (if any subteam restricts a link, it's restricted)
                        if (contentRestrictions.links) {
                            Object.keys(contentRestrictions.links).forEach(linkType => {
                                if (contentRestrictions.links[linkType]?.restricted === true) {
                                    mergedRestrictions.restrictedLinks[linkType] = true;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error(`Error loading restrictions from subteam ${subteamId}:`, error);
                }
            }

            setContentRestrictions(mergedRestrictions);
        } catch (error) {
            console.error('Error loading content restrictions:', error);
        } finally {
            setLoadingRestrictions(false);
        }
    }, [userId, isTemporaryCard]);

    // Load restrictions when component mounts
    useEffect(() => {
        loadContentRestrictions();
    }, [loadContentRestrictions]);

    // Helper function to check if a link type is restricted
    const isLinkTypeRestricted = useCallback((linkType) => {
        if (isTeamAdmin || isSubteamAdmin) return false; // Admins are not restricted
        if (contentRestrictions.restrictAll) return true; // If restrictAll is enabled, all links are restricted
        return contentRestrictions.restrictedLinks[linkType] === true;
    }, [contentRestrictions, isTeamAdmin, isSubteamAdmin]);

    // ✅ ADD THIS TEMPORARY DEBUG SECTION
    useEffect(() => {
        console.log('🔍 LINKS_CONTAINER_DEBUG:', {
            cardId: cardId?.substring(0, 12) + '...',
            cardIdLength: cardId?.length,
            isUUID: cardId && cardId.includes('-'),
            isTemporaryCard,
            contactLinksCount: contactLinks?.length || 0,
            contactLinksTypes: contactLinks?.map(l => l.type) || [],
            timestamp: new Date().toISOString()
        });
    }, [cardId, contactLinks, isTemporaryCard]);

    // Load fresh card data from server when component mounts
    useEffect(() => {
        const loadFreshCardData = async () => {
            if (cardId && !contextIsTemporaryCard(cardId)) {
                try {
                    console.log('🔍 TeamContactLinksContainer: Loading fresh card data from server');
                    await loadCardFromServer(cardId);
                } catch (error) {
                    console.error('🔍 TeamContactLinksContainer: Error loading fresh card data:', error);
                }
            }
        };

        loadFreshCardData();
    }, [cardId, contextIsTemporaryCard]); // ✅ REMOVED loadCardFromServer from dependencies

    useEffect(() => {
        if (cardId) {
            console.log('🔍 TeamContactLinksContainer: Setting current card:', cardId);
            console.log('🔍 TeamContactLinksContainer: Card creation check:', {
                cardId,
                isTemporaryCard,
                cardIdLength: cardId.length,
                hasHyphens: cardId.includes('-')
            });
            setCurrentCard(cardId);

            // ✅ FIXED: Check for links under both current card ID and tempCardId
            const hasContactLinksForThisCard = contactLinks && contactLinks.length > 0;
            const hasTempCardLinks = contactLinksByCard['tempCardId'] && contactLinksByCard['tempCardId'].length > 0;

            console.log('🔍 TeamContactLinksContainer: Link availability check:', {
                currentCardId: cardId?.substring(0, 12) + '...',
                hasContactLinksForThisCard,
                hasTempCardLinks,
                tempCardLinksCount: contactLinksByCard['tempCardId']?.length || 0,
                currentCardLinksCount: contactLinks?.length || 0
            });

            if (!hasContactLinksForThisCard && !hasTempCardLinks) {
                console.log('🔍 TeamContactLinksContainer: No contact links found for card, fetching...');
                fetchContactLinksForCard(cardId);
            } else {
                console.log('🔍 TeamContactLinksContainer: Contact links available:', {
                    currentCard: contactLinks?.length || 0,
                    tempCard: contactLinksByCard['tempCardId']?.length || 0
                });
            }
        }
    }, [cardId, contactLinks, setCurrentCard, fetchContactLinksForCard]);

    // Sync context values when they change
    useEffect(() => {
        if (cardId && cardState[cardId]) {
            console.log('🔍 TeamContactLinksContainer: Context values updated:', {
                cardTheme: cardState[cardId]?.cardTheme,
                cardLinkColor: cardState[cardId]?.cardLinkColor,
                cardName: cardState[cardId]?.cardName
            });
        }
    }, [cardId, cardState]);

    // Card data from context
    const profilePictureBlob = cardState[cardId]?.cardProfilePictureImage;
    const cardFullName = cardState[cardId]?.cardFullName || '';
    const cardJobTitle = cardState[cardId]?.cardJobTitle || '';
    const cardCompany = cardState[cardId]?.cardCompany || '';
    const cardLocation = cardState[cardId]?.cardLocation || '';
    const cardBio = cardState[cardId]?.cardBio || '';
    const cardLayout = cardState[cardId]?.contextCardLayout || 'left-aligned';
    const cardTheme = cardState[cardId]?.cardTheme || '';
    const cardLinkColor = cardState[cardId]?.cardLinkColor || '';
    const cardProfilePictureUrl = cardState[cardId]?.cardProfilePictureUrl;
    const cardCoverPhotoUrl = cardState[cardId]?.cardCoverPhotoUrl;
    const cardCompanyLogoUrl = cardState[cardId]?.cardCompanyLogoUrl;
    const cardName = cardState[cardId]?.cardName || '';

    const handleToggleVisibility = async (linkId, isVisible) => {
        console.log('Toggling visibility for link ID:', linkId, 'to:', isVisible);
        try {
            await contextUpdateContactLink(linkId, cardId, { isVisible }, true);
            console.log('Visibility updated successfully');
        } catch (error) {
            console.error('Error toggling visibility:', error);
        }
    };

    const handleDelete = async (linkId) => {
        console.log('Deleting link ID:', linkId);
        try {
            // Find the link being deleted to check if it's a call link
            const linkToDelete = contactLinks.find(link => link._id === linkId || link.id === linkId);
            const isCallLink = linkToDelete && isPhoneContactType(linkToDelete.type);

            await contextDeleteContactLink(linkId, cardId, userId, true);
            console.log('Contact link deleted successfully');

            // ✅ PHONE NUMBER SYNC: If a call link was deleted, update card phone number from remaining call links
            if (isCallLink && cardId) {
                try {
                    // Wait a bit for the context to update
                    setTimeout(async () => {
                        // Get updated contact links after deletion (from context)
                        const updatedLinks = contactLinksByCard[cardId] || [];

                        // Find remaining call links
                        const remainingCallLinks = updatedLinks.filter(link => isPhoneContactType(link.type));

                        if (remainingCallLinks.length > 0) {
                            // Update card phone number to first remaining call link's phone number
                            const firstCallLink = remainingCallLinks[0];
                            const phoneNumber = firstCallLink.details?.linkUrl || firstCallLink.details?.phone;

                            if (phoneNumber) {
                                console.log('🔍 Updating card phone number from remaining call link:', phoneNumber);

                                await updateCard(cardId, {
                                    contact_info: {
                                        phone: phoneNumber
                                    }
                                });

                                // Update card data in context
                                if (updateCardData) {
                                    updateCardData(cardId, {
                                        cardPhoneNumber: phoneNumber
                                    }, false);
                                }

                                console.log('✅ Card phone number updated from remaining call link');
                            }
                        } else {
                            // No call links remaining, remove phone number from card
                            console.log('🔍 No call links remaining, removing phone number from card');

                            await updateCard(cardId, {
                                contact_info: {
                                    phone: ''
                                }
                            });

                            // Update card data in context
                            if (updateCardData) {
                                updateCardData(cardId, {
                                    cardPhoneNumber: ''
                                }, false);
                            }

                            console.log('✅ Card phone number removed');
                        }
                    }, 500); // Wait for context to update
                } catch (error) {
                    console.error('🔍 Error syncing card phone number after deletion:', error);
                    // Don't throw - this is not critical for deletion
                }
            }

            setEditingLink(null);
        } catch (error) {
            console.error('Error deleting contact link:', error);
        }
    };

    const handleEditLink = (link) => {
        console.log('Editing link:', link);
        setEditingLink(link);
    };

    const handleUpdateLink = async (linkId, updatedData) => {
        try {
            console.log('🔍 TeamContactLinksContainer: Starting contact link update');
            console.log('🔍 TeamContactLinksContainer: linkId:', linkId);
            console.log('🔍 TeamContactLinksContainer: cardId:', cardId);
            console.log('🔍 TeamContactLinksContainer: updatedData:', updatedData);
            console.log('🔍 TeamContactLinksContainer: current contactLinks:', contactLinks);

            // Check if type is being changed and if new type is restricted
            if (updatedData.type) {
                const existingLink = contactLinks.find(link => link._id === linkId);
                if (existingLink && existingLink.type !== updatedData.type) {
                    if (isLinkTypeRestricted(updatedData.type)) {
                        setNotification({
                            open: true,
                            message: `You cannot change to "${updatedData.type}" contact links as it is restricted by your subteam.`,
                            severity: 'warning'
                        });
                        return;
                    }
                }
            }

            await contextUpdateContactLink(linkId, cardId, updatedData, true);
            console.log('🔍 TeamContactLinksContainer: Contact link updated successfully');
            setEditingLink(null);
            setShouldRefresh(true);
        } catch (error) {
            console.error('🔍 TeamContactLinksContainer: Error updating contact link:', error);
            console.error('🔍 TeamContactLinksContainer: Error details:', {
                message: error.message,
                stack: error.stack,
                response: error.response?.data
            });

            // Handle 403 errors from backend
            if (error.response?.status === 403 && error.response?.data?.message) {
                setNotification({
                    open: true,
                    message: error.response.data.message,
                    severity: 'error'
                });
            }
        }
    };

    const handleContactLinkButtonClick = (item) => {
        // Check if link type is restricted
        if (isLinkTypeRestricted(item)) {
            setNotification({
                open: true,
                message: `"${item}" contact links are restricted by your subteam and cannot be added.`,
                severity: 'warning'
            });
            return;
        }

        setContactLinkType(item);
        setSelectedLinkItem(item);
        setContactLinksDetailModalOpen(true);
    };

    // ✅ DEBUG FUNCTION - FOR DEVELOPMENT TROUBLESHOOTING
    // This function provides comprehensive debugging of contact link state and context.
    // It logs detailed information about card IDs, contact links, and context state.
    // Used during development to diagnose contact link issues.
    // The debug button that calls this function is commented out above.
    const debugContactLinks = () => {
        console.log('🔍 =================DEBUG CONTACT LINKS=================');
        console.log('🔍 PROVIDED_CARD_ID:', cardId?.substring(0, 12) + '...');
        console.log('🔍 CURRENT_CARD_ID:', currentCardId?.substring(0, 12) + '...');
        console.log('🔍 CARD_ID_MATCH:', cardId === currentCardId);
        console.log('🔍 ALL_CARD_IDS:', Object.keys(contactLinksByCard).map(id => id.substring(0, 12) + '...'));
        console.log('🔍 LINKS_FOR_PROVIDED_CARD:', contactLinksByCard[cardId]?.length || 0);
        console.log('🔍 LINKS_FOR_CURRENT_CARD:', contactLinksByCard[currentCardId]?.length || 0);
        console.log('🔍 CONTEXT_CONTACT_LINKS:', contactLinks?.length || 0);
        console.log('🔍 =======================================================');

        if (debugCurrentState) {
            debugCurrentState();
        }
    };

    // ✅ CONSOLE DEBUG INFO - AUTOMATIC LOGGING IN DEVELOPMENT
    // This useEffect automatically logs key state information to console during development.
    // It helps track card ID changes, temporary card status, and contact link counts.
    // Only runs in development mode and logs when cardId, isTemporaryCard, or contactLinks change.
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔍 TeamContactLinksContainer Debug Info:', {
                cardId: cardId?.substring(0, 12) + '...',
                cardIdLength: cardId?.length,
                isUUID: cardId && cardId.includes('-'),
                isTemporary: isTemporaryCard,
                contactLinksCount: contactLinks?.length || 0,
                contactLinksTypes: contactLinks?.map(l => l.type).join(', ') || 'None'
            });
        }
    }, [cardId, isTemporaryCard, contactLinks]);

    const handleImagesUpload = async (newCardId) => {
        try {
            if (cardState[cardId]?.cardProfilePictureUrl) {
                const blob = await fetch(cardState[cardId].cardProfilePictureUrl).then(r => r.blob());
                const profilePicUrl = await uploadImageBlob(
                    blob,
                    setCardProfilePictureUrl,
                    'card',
                    'profile',
                    newCardId
                );
                await updateCardProfilePictureUrl(newCardId, profilePicUrl);
            }

            if (cardState[cardId]?.cardCoverPhotoUrl) {
                const blob = await fetch(cardState[cardId].cardCoverPhotoUrl).then(r => r.blob());
                const coverPhotoUrl = await uploadImageBlob(
                    blob,
                    setCardCoverPhotoUrl,
                    'card',
                    'cover',
                    newCardId
                );
                await updateCardCoverPhotoUrl(newCardId, coverPhotoUrl);
            }

            if (cardState[cardId]?.cardCompanyLogoUrl) {
                const blob = await fetch(cardState[cardId].cardCompanyLogoUrl).then(r => r.blob());
                const companyLogoUrl = await uploadImageBlob(
                    blob,
                    setCardCompanyLogoUrl,
                    'card',
                    'logo',
                    newCardId
                );
                await updateCardCompanyLogoUrl(newCardId, companyLogoUrl);
            }
        } catch (error) {
            console.error('Error uploading images:', error);
            throw error;
        }
    };

    // PRODUCTION-READY: Enhanced save card function
    const handleSaveCard = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setLoading(true);

        try {
            if (!cardFullName) {
                alert('Card name is required. Please go back to the About tab and enter at least the card name.');
                return;
            }

            updateCardData(cardId, {
                cardFullName,
                cardJobTitle,
                cardCompany,
                cardLocation,
                cardBio,
                contextCardLayout: cardLayout,
                cardTheme,
                cardLinkColor
            });

            const cardDetails = {
                fullName: cardFullName,
                jobTitle: cardJobTitle || '',
                company: cardCompany || '',
                location: cardLocation || '',
                bio: cardBio || '',
                cardLayout: cardLayout || 'left-aligned',
                cardTheme: cardTheme || '',
                cardName: cardName || 'My Card',
                linkColor: cardLinkColor || '',
                userId: userId,
                teamId: currentTeamId, // ✅ ADD: Include teamId for team context
                isActive: true,
                is_default: false,
                autoFollowUpIntroEmail: getDefaultFollowUpEmail(),
            };

            console.log('🚀 CARD_CREATION: Creating permanent card with data:', cardDetails);
            console.log('🚀 CARD_CREATION: Context values read:', {
                cardTheme,
                cardLinkColor,
                cardName,
                cardState: cardState[cardId]
            });
            console.log('🚀 CARD_CREATION: Current UUID card ID:', cardId);

            // Step 1: Create permanent card
            const response = await createCard(cardDetails);
            console.log('✅ CARD_CREATED: Permanent card created:', response);

            if (!response || !response.card || !response.card._id) {
                throw new Error('Failed to create card - invalid response from server');
            }

            const permanentCardId = response.card._id;
            console.log('✅ PERMANENT_CARD_ID:', permanentCardId);

            // Step 2: Upload images
            await handleImagesUpload(permanentCardId);

            // ✅ FIXED: Step 3 - UUID Contact Links Transfer
            try {
                console.log('🔄 TRANSFER_START: Starting UUID contact links transfer...');
                console.log('🔄 TRANSFER_PARAMS:', {
                    fromCardId: cardId?.substring(0, 12) + '...',
                    toCardId: permanentCardId?.substring(0, 8) + '...',
                    userId: userId?.substring(0, 10) + '...',
                    currentContactLinksCount: contactLinks?.length || 0
                });

                // ✅ FIXED: Check for links under both current card ID and tempCardId
                const currentContactLinks = contactLinks || [];
                const tempCardLinks = contactLinksByCard['tempCardId'] || [];
                const allLinksToTransfer = [...currentContactLinks, ...tempCardLinks];

                console.log('🔍 TRANSFER_LINK_CHECK:', {
                    currentContactLinks: currentContactLinks.length,
                    tempCardLinks: tempCardLinks.length,
                    allLinksToTransfer: allLinksToTransfer.length,
                    currentCardId: cardId?.substring(0, 12) + '...',
                    tempCardId: 'tempCardId'
                });

                if (allLinksToTransfer.length === 0) {
                    console.log('ℹ️ NO_LINKS_TO_TRANSFER: Skipping transfer');
                } else {
                    console.log('🔄 TRANSFERRING_LINKS: Found links to transfer:', allLinksToTransfer.length);

                    // ✅ CRITICAL FIX: Use the correct transfer function for UUID cards
                    // Pass null as sourceCardId to let the function auto-detect the source
                    const transferResponse = await saveAllContactLinksToDatabase(
                        permanentCardId,  // Permanent card ID (destination)
                        userId,           // User ID
                        null             // Let function auto-detect source (tempCardId or UUID)
                    );

                    console.log('🔄 TRANSFER_RESPONSE:', transferResponse);

                    if (transferResponse && transferResponse.success) {
                        console.log('✅ TRANSFER_SUCCESS:', {
                            transferred: transferResponse.saved || transferResponse.transferred,
                            total: transferResponse.total,
                            links: transferResponse.links?.length
                        });

                        // Verify transfer worked
                        try {
                            await new Promise(resolve => setTimeout(resolve, 1500)); // Longer delay for UUID transfer

                            const verificationResponse = await axios.get(`${API_BASE_URL}/api/contact-link/card/${permanentCardId}`);
                            console.log('🔍 TRANSFER_VERIFICATION:', verificationResponse.data);

                            if (verificationResponse.data.success && verificationResponse.data.contactLinks.length > 0) {
                                console.log('✅ VERIFICATION_PASSED: Transfer successful');
                            } else {
                                console.warn('⚠️ VERIFICATION_FAILED: No permanent links found');
                            }
                        } catch (verifyError) {
                            console.warn('⚠️ VERIFICATION_ERROR:', verifyError);
                        }

                        // Clear cache and refresh
                        clearFetchedCards();
                        await fetchContactLinksForCard(permanentCardId);

                    } else {
                        console.error('❌ TRANSFER_FAILED:', transferResponse);
                        throw new Error('Transfer failed with unsuccessful response');
                    }
                }

            } catch (transferError) {
                console.error('❌ TRANSFER_ERROR:', transferError);
                console.error('❌ TRANSFER_ERROR_DETAILS:', {
                    message: transferError.message,
                    response: transferError.response?.data,
                    status: transferError.response?.status
                });

                // ✅ IMPROVED: Better error handling - still continue but warn user
                console.warn('⚠️ TRANSFER_FAILED_CONTINUING: Transfer failed but card creation will continue');

                // Don't fail the entire card creation, but log the issue
                // The user will have a card but may need to recreate contact links
            }

            // Step 4: Update card context
            updateCardId(permanentCardId);

            // Step 5: Navigate with proper timing
            console.log('✅ CARD_CREATION_COMPLETE: Process completed successfully');

            // Delay navigation to ensure all processes complete
            setTimeout(() => {
                navigate('/team/members');
            }, 2000); // ✅ Increased delay for UUID transfer

        } catch (error) {
            console.error('❌ CARD_CREATION_FAILED:', error);
            alert('Failed to create card. Please try again.');
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    };

    const handleNavigateToAbout = () => {
        updateCardData(cardId, {
            cardFullName,
            cardJobTitle,
            cardCompany,
            cardLocation,
            cardBio,
            contextCardLayout: cardLayout,
            cardTheme,
            cardLinkColor
        });

        if (setSelectedItem) {
            setSelectedItem('about');
        }

        navigate(`/team/members/card/add-card/${cardId}?section=about`);
    };

    if (editingLink) {
        return (
            <Box display="flex" alignItems="start" flexDirection="column" gap={2}>
                <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginTop: 2 }}>
                    Edit Link
                </Typography>
                <TeamUpdateContactLink
                    link={editingLink}
                    onCancel={() => setEditingLink(null)}
                    onUpdate={handleUpdateLink}
                    onDelete={handleDelete}
                />
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="start" flexDirection="column" gap={2}>
            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginTop: 2 }}>
                Links
            </Typography>

            {/* ✅ DEBUG BUTTON - COMMENTED OUT FOR PRODUCTION
          This button was used during development to manually trigger detailed debugging
          of contact link state and context. It logs comprehensive debug info to console.
          Uncomment if you need to debug contact link issues in development.
      */}
            {/* 
      {process.env.NODE_ENV === 'development' && (
        <Button
          onClick={debugContactLinks}
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            backgroundColor: '#ffeb3b',
            color: '#000',
            '&:hover': { backgroundColor: '#fff176' }
          }}
        >
          🔍 DEBUG CONTACT LINKS
        </Button>
      )}
      */}

            <TeamLinksToggle onAddLinksAndContact={() => setOpenModal(true)} />

            {/* ✅ DEBUG INFO MOVED TO CONSOLE - HIDDEN UI ELEMENT
          This was previously a visible debug box in the UI.
          Debug information is now logged to console instead for cleaner UI.
          The hidden Box element is kept for potential future use.
      */}
            {process.env.NODE_ENV === 'development' && (
                <Box sx={{ display: 'none' }}>
                    {/* Hidden debug info - logged to console instead */}
                </Box>
            )}

            {/* ✅ FIXED: Smart link combination - only combine for new card creation */}
            {(() => {
                let linksToDisplay;

                if (isTemporaryCard) {
                    // ✅ NEW CARD CREATION: Combine links from both sources
                    const currentCardLinks = contactLinks || [];
                    const tempCardLinks = contactLinksByCard['tempCardId'] || [];
                    linksToDisplay = [...currentCardLinks, ...tempCardLinks];

                    console.log('🔍 ContactLinksList: NEW CARD - Combined links:', {
                        currentCardLinks: currentCardLinks.length,
                        tempCardLinks: tempCardLinks.length,
                        allLinks: linksToDisplay.length,
                        linkTypes: linksToDisplay.map(l => l.type)
                    });
                } else {
                    // ✅ EXISTING CARD: Use only database links
                    linksToDisplay = contactLinks || [];

                    console.log('🔍 ContactLinksList: EXISTING CARD - Database links:', {
                        linksCount: linksToDisplay.length,
                        linkTypes: linksToDisplay.map(l => l.type)
                    });
                }

                return (
                    <TeamContactLinksList
                        contactLinks={linksToDisplay}
                        onToggleVisibility={handleToggleVisibility}
                        onDelete={handleDelete}
                        onEdit={handleEditLink}
                        disableEditDelete={false}
                    />
                );
            })()}

            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mt={4}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: "#4f4f4f" }}>
                    Recommended Links
                </Typography>

                <Button
                    onClick={() => setOpenModal(true)}
                    sx={{
                        color: '#2aaff7',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#d9f1ff' },
                    }}
                    endIcon={<AddIcon sx={{ transform: 'scaleX(-1)' }} />}
                >
                    Explore all Links
                </Button>
            </Box>

            <TeamRecommendedLinks
                onContactLinkButtonClick={handleContactLinkButtonClick}
                isLinkTypeRestricted={isLinkTypeRestricted}
            />

            {/* Navigation and Save Buttons */}
            {isTemporaryCard && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={handleNavigateToAbout}
                        sx={{
                            padding: '10px 30px',
                            color: '#000000',
                            fontWeight: "bold",
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#000000',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                borderColor: '#000000',
                            },
                        }}
                    >
                        Back
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        onClick={handleSaveCard}
                        sx={{
                            backgroundColor: '#000000',
                            padding: '10px 30px',
                            color: '#ffffff',
                            fontWeight: "bold",
                            textTransform: 'none',
                            borderRadius: '10px',
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Card'}
                    </Button>
                </Box>
            )}

            <TeamContactLinkDetailModal
                open={contactLinksDetailModalOpen}
                handleClose={() => {
                    setContactLinksDetailModalOpen(false);
                    setShouldRefresh(true);
                }}
                userId={userId}
                cardId={cardId}
                contactLinkType={contactLinkType}
                isTemporaryCard={isTemporaryCard}
                existingContactLinks={contactLinks}
                isInSignupFlow={isInSignupFlow}
            />

            <TeamAddContactLinkModal
                open={openModal}
                handleClose={() => {
                    setOpenModal(false);
                    setShouldRefresh(true);
                }}
                userId={userId}
                cardId={cardId}
                isTemporaryCard={isTemporaryCard}
                existingContactLinks={contactLinks}
                isInSignupFlow={isInSignupFlow}
                isLinkTypeRestricted={isLinkTypeRestricted}
                onRestrictionNotification={(message) => {
                    setNotification({
                        open: true,
                        message,
                        severity: 'warning'
                    });
                }}
            />

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
            />
        </Box>
    );
};

export default TeamContactLinksContainer;